import { supabase } from '@/integrations/supabase/client';

export interface DreamIdea {
  id: string;
  title: string;
  name: string;
  email: string;
  twitterHandle?: string;
  description: string;
  mockupUrl?: string;
  videoUrl?: string;
  tags: string[];
  votes: number;
  createdAt: Date;
  trending?: boolean;
}

// Database row type
interface DreamRow {
  id: string;
  title: string;
  name: string;
  email: string;
  twitter_handle: string | null;
  description: string;
  mockup_url: string | null;
  video_url: string | null;
  tags: string[];
  votes: number;
  created_at: string;
  trending: boolean;
}

// Convert database row to DreamIdea
function rowToDream(row: DreamRow): DreamIdea {
  return {
    id: row.id,
    title: row.title,
    name: row.name,
    email: row.email,
    twitterHandle: row.twitter_handle || undefined,
    description: row.description,
    mockupUrl: row.mockup_url || undefined,
    videoUrl: row.video_url || undefined,
    tags: row.tags || [],
    votes: row.votes || 0,
    createdAt: new Date(row.created_at),
    trending: row.trending || false
  };
}

// Realtime subscription listeners
let listeners: (() => void)[] = [];

// Subscribe to realtime changes
supabase
  .channel('dreams_changes')
  .on('postgres_changes', { event: '*', schema: 'public', table: 'dreams' }, () => {
    console.log('🔔 Realtime update detected');
    listeners.forEach(listener => listener());
  })
  .subscribe();

export const dreamService = {
  // Get all dreams
  async getAllDreams(): Promise<DreamIdea[]> {
    console.log('📚 Fetching all dreams from Supabase');
    
    const { data, error } = await supabase
      .from('dreams')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ Error fetching dreams:', error);
      return [];
    }

    console.log(`✅ Fetched ${data.length} dreams from database`);
    return data.map(rowToDream);
  },

  // Get a single dream by ID
  async getDreamById(id: string): Promise<DreamIdea | undefined> {
    console.log('🔍 Fetching dream by ID:', id);
    
    const { data, error } = await supabase
      .from('dreams')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('❌ Error fetching dream:', error);
      return undefined;
    }

    return rowToDream(data);
  },

  // Submit a new dream
  async submitDream(dream: Omit<DreamIdea, 'id' | 'votes' | 'createdAt'>): Promise<DreamIdea> {
    console.log('🚀 Submitting new dream:', dream.title);

    // Auto-generate tags from description and title
    const text = `${dream.title} ${dream.description}`.toLowerCase();
    const possibleTags = ['AI', 'DevTools', 'Productivity', 'Marketplace', 'Fintech', 
                          'Remote Work', 'Collaboration', 'Web3', 'HealthTech', 
                          'Security', 'Blockchain', 'Crypto', 'SaaS', 'API'];
    const autoTags = possibleTags.filter(tag => 
      text.includes(tag.toLowerCase())
    ).slice(0, 3);

    const dreamData = {
      title: dream.title,
      name: dream.name,
      email: dream.email,
      twitter_handle: dream.twitterHandle || null,
      description: dream.description,
      mockup_url: dream.mockupUrl || null,
      video_url: dream.videoUrl || null,
      tags: dream.tags.length > 0 ? dream.tags : autoTags
    };

    const { data, error } = await supabase
      .from('dreams')
      .insert([dreamData])
      .select()
      .single();

    if (error) {
      console.error('❌ Error submitting dream:', error);
      throw new Error('Failed to submit dream');
    }

    console.log('✅ Dream submitted successfully!');
    return rowToDream(data);
  },

  // Vote for a dream
  async voteDream(id: string): Promise<DreamIdea | undefined> {
    console.log('❤️ Voting for dream:', id);
    
    // First get current votes
    const { data: dream, error: fetchError } = await supabase
      .from('dreams')
      .select('votes')
      .eq('id', id)
      .single();

    if (fetchError) {
      console.error('❌ Error fetching dream for voting:', fetchError);
      return undefined;
    }

    // Increment votes
    const { data, error } = await supabase
      .from('dreams')
      .update({ votes: (dream.votes || 0) + 1 })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('❌ Error updating votes:', error);
      return undefined;
    }

    console.log('✅ Vote recorded! New vote count:', data.votes);
    return rowToDream(data);
  },

  // Subscribe to changes
  subscribe(callback: () => void): () => void {
    listeners.push(callback);
    return () => {
      listeners = listeners.filter(l => l !== callback);
    };
  }
};
